/**
 * Read-boundary normalization for Markup records.
 *
 * All external sources — Firestore, PDF sidecar, future REST imports — must
 * pass raw data through `normalizeMarkupRead` before it enters any in-memory
 * state. This guarantees the rest of the app always sees canonical types:
 *  - Dates are real Date objects (not ISO strings)
 *  - `style.textSize` is always set when the markup carries text (the legacy
 *    `style.fontSize` number is converted and cleared)
 *  - `layer_id` is always populated (see below)
 *  - Structurally invalid records are dropped (returns null)
 *
 * `layer_id` is a STORED FACT, not something resolved at display time. Producer-
 * and legacy-authored markups can arrive without one; they are stamped with the
 * default markup layer here. Previously absence was resolved on read against the
 * ACTIVE layer, which is not a property of the markup at all -- so a mark with no
 * layer_id appeared to move between layers as the user changed the active one,
 * and a count badge using a different fallback disagreed with the list it
 * labelled. Normalizing once at the boundary removes both.
 *
 * FirestoreService already validates with Zod; this module adds the additional
 * semantic normalization layer on top of the structural check. The PDF sidecar
 * path (`core/pdf-import`) was the only source that skipped validation
 * entirely — it now routes through `normalizeMarkupRead`.
 */
import { MarkupSchema } from '@/types/schemas';
import { DEFAULT_MARKUP_LAYER_ID } from '@/core/markups/layers';
import { TextSize } from '@/types/enums';
import { TEXT_SIZE_MAP } from '@/core/theme/typography';
import type { Markup, MarkupStyle } from '@/types';
import { createLogger } from '@/utils/logger';

const log = createLogger('normalizeMarkup');

// ── Style normalization ────────────────────────────────────────────────────

/**
 * Derive a TextSize enum from a legacy numeric `fontSize` value.
 *
 * The three canonical sizes are 10, 14, 20. Any value ≤ 10 maps to SMALL,
 * ≥ 18 maps to LARGE (half-way between MEDIUM and LARGE), and the rest to
 * MEDIUM. This correctly rounds the exact TEXT_SIZE_MAP values and handles
 * arbitrary legacy entries without data loss.
 */
function fontSizeToTextSize(fontSize: number): TextSize {
    if (fontSize <= 10) return TextSize.SMALL;
    if (fontSize >= 18) return TextSize.LARGE;
    return TextSize.MEDIUM;
}

/**
 * Normalize a markup style object.
 *
 * If `textSize` is absent but `fontSize` is present, the pixel value is
 * converted to the nearest TextSize enum and `fontSize` is removed. This
 * ensures downstream code can always rely on `textSize` for text-bearing
 * markups and never needs the legacy numeric fallback.
 *
 * If `textSize` is already set, the style is returned as-is (no conversion).
 */
export function normalizeMarkupStyle(style: MarkupStyle): MarkupStyle {
    if (style.textSize || !style.fontSize) return style;
    const { fontSize, ...rest } = style;
    return { ...rest, textSize: fontSizeToTextSize(fontSize) };
}

// ── Date coercion ──────────────────────────────────────────────────────────

/**
 * Revive ISO date strings in a raw object so the Zod `z.date()` fields parse.
 *
 * Firestore returns real Timestamp objects (converted by the SDK); sidecar
 * JSON has ISO strings. Both reach `normalizeMarkupRead` — this guard handles
 * the sidecar case without breaking the Firestore case.
 */
function coerceDates(raw: unknown): unknown {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
    const obj = raw as Record<string, unknown>;
    const revive = (v: unknown) =>
        v instanceof Date ? v : typeof v === 'string' ? new Date(v) : new Date();
    return {
        ...obj,
        createdAt: revive(obj.createdAt),
        updatedAt: revive(obj.updatedAt),
    };
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Validate and normalize a raw value from any external source into a Markup.
 *
 * Returns null (and logs a warning) when the record fails structural
 * validation — callers should `.filter(Boolean)` the results.
 *
 * @param raw      Raw object from JSON.parse, Firestore, etc.
 * @param context  Optional string added to the warning (e.g. 'pdf-sidecar').
 */
export function normalizeMarkupRead(raw: unknown, context?: string): Markup | null {
    const coerced = coerceDates(raw);
    const result = MarkupSchema.safeParse(coerced);
    if (!result.success) {
        log.warn(
            `dropping malformed markup${context ? ` (${context})` : ''}`,
            result.error.issues,
        );
        return null;
    }
    const m = result.data;
    return {
        ...m,
        style: normalizeMarkupStyle(m.style),
        layer_id: m.layer_id ?? DEFAULT_MARKUP_LAYER_ID,
    };
}

/**
 * Resolve the pixel size for a text-bearing markup style.
 *
 * Prefers `textSize` (canonical), falls back to the legacy numeric `fontSize`,
 * then defaults to the MEDIUM size. Call sites that have already been through
 * `normalizeMarkupRead` will always hit the first branch.
 */
export function resolveMarkupFontSize(style: MarkupStyle): number {
    if (style.textSize) return TEXT_SIZE_MAP[style.textSize];
    if (style.fontSize) return style.fontSize;
    return TEXT_SIZE_MAP[TextSize.MEDIUM];
}
